import { useState } from "react";

import { DailyRanking, Info24px, SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { TOPICS_HEATMAP_STATE_NAME } from "app/pages/market-research/shared/constants";
import { allTrackers } from "app/services/track/track";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useTopicsData } from "../../api-client/demandSearchTrendsTopicsAPI";

import { TopicsDiscoverAllTopicsModal } from "./TopicsDiscoverAllTopicsModal/TopicsDiscoverAllTopicsModal";
import type { TrendingTopicsData } from "./types";

type TopicsDiscoverHeaderProps = {
    country: number;
    userTopic: TrendingTopicsData["topic"];
    isLoadingTrendingTopics: boolean;
};

export const TopicsDiscoverHeader = ({ country, userTopic, isLoadingTrendingTopics }: TopicsDiscoverHeaderProps) => {
    const t = useTranslation();
    const { isLoading: isLoadingTopics } = useTopicsData();
    const [isModalOpen, setModalIsOpen] = useState(false);

    const discoverCategory = userTopic?.path ?? "general";
    const predefinedUserTopic = userTopic?.path?.split("/")[0];

    const handleExploreAllClick = () => {
        setModalIsOpen(true);

        allTrackers.dispatchTrackEvent("Button", "click", "Discover Explore All", {
            discover_category: discoverCategory,
        });
        allTrackers.dispatchTrackEvent("Pop Up", "open", "Topics Tree Modal", {
            discover_category: discoverCategory,
        });
    };

    const handleSeeTrendsClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Discover See Topic Trends", {
            discover_category: discoverCategory,
        });

        void proNavigator.go(TOPICS_HEATMAP_STATE_NAME, { country });
    };

    const tooltipContent = userTopic?.domain
        ? t("demand.analysis.home.discover.header.title.tooltip", { domain: userTopic.domain })
        : t("demand.analysis.home.discover.header.title.tooltip.allCategories");

    return (
        <Flex
            justifyContent="space-between"
            alignItems="center"
            gap="16px"
        >
            <Flex
                alignItems="center"
                gap="24px"
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Text
                        as="h5"
                        fontVariant="h5"
                        color={colorsPalettes.carbon[500]}
                        data-automation="topics-discover-header"
                    >
                        {t("demand.analysis.home.discover.header.title")}
                    </Text>
                    <Box
                        height="20px"
                        width="20px"
                    >
                        <Tooltip
                            placement="top"
                            content={tooltipContent}
                        >
                            <Info24px
                                size="sm"
                                color={colorsPalettes.carbon[200]}
                            />
                        </Tooltip>
                    </Box>
                </Flex>
                <Flex gap="16px">
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <DailyRanking
                            size="xs"
                            color={colorsPalettes.carbon[200]}
                        />
                        <Text
                            fontVariant="chip1"
                            color={colorsPalettes.carbon[400]}
                            data-automation="last-28-days"
                        >
                            {t("demand_analysis.topics_discover.duration.l28d")}
                        </Text>
                    </Flex>
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <SWReactCountryIcons
                            countryCode={country}
                            size="xs"
                        />
                        <Text
                            fontVariant="chip1"
                            color={colorsPalettes.carbon[400]}
                            data-automation="worldwide-country"
                        >
                            {t(`common.country.${country}`)}
                        </Text>
                    </Flex>
                </Flex>
            </Flex>
            <Flex
                gap="8px"
                alignItems="center"
            >
                <IconButton
                    type="outlined"
                    iconName="trend-up"
                    iconColor={colorsPalettes.blue[400]}
                    isDisabled={isLoadingTrendingTopics || isLoadingTopics}
                    onClick={handleExploreAllClick}
                    dataAutomation="topics-discover-explore-all"
                >
                    {t("demand.analysis.home.discover.header.explore.button")}
                </IconButton>
                <IconButton
                    type="outlined"
                    iconName="arrow-right"
                    placement="right"
                    iconColor={colorsPalettes.blue[400]}
                    isDisabled={isLoadingTrendingTopics || isLoadingTopics}
                    onClick={handleSeeTrendsClick}
                    dataAutomation="topics-discover-see-trends"
                >
                    {t("demand.analysis.home.discover.header.see_trends.button")}
                </IconButton>
            </Flex>
            <TopicsDiscoverAllTopicsModal
                isOpen={isModalOpen}
                onClose={() => setModalIsOpen(false)}
                predefinedUserTopic={predefinedUserTopic}
                discoverCategory={discoverCategory}
            />
        </Flex>
    );
};
